'use client';

/**
 * Device Fingerprint Service
 * Generates and manages device fingerprint data for API authentication
 */

interface DeviceFingerprint {
  deviceId: string;
  deviceName: string;
  deviceType: string;
  osName: string;
  osVersion: string;
  browserName: string;
  browserVersion: string;
  ipAddress: string;
}

class DeviceFingerprintService {
  private static instance: DeviceFingerprintService;
  private fingerprint: DeviceFingerprint | null = null;

  private constructor() {}

  public static getInstance(): DeviceFingerprintService {
    if (!DeviceFingerprintService.instance) {
      DeviceFingerprintService.instance = new DeviceFingerprintService();
    }
    return DeviceFingerprintService.instance;
  }

  /**
   * Generate a unique device ID based on browser characteristics
   */
  private generateDeviceId(): string {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint', 2, 2);
    }
    
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width,
      screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      canvas.toDataURL()
    ].join('|');

    // Generate a hash-like ID from the fingerprint
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return Math.abs(hash).toString(16).padStart(16, '0').substring(0, 16);
  }

  /**
   * Detect device type based on user agent
   */
  private getDeviceType(): string {
    const ua = navigator.userAgent.toLowerCase();
    if (/mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua)) {
      if (/ipad/i.test(ua)) return 'tablet';
      return 'mobile';
    }
    return 'desktop';
  }

  /**
   * Get device name based on user agent
   */
  private getDeviceName(): string {
    const ua = navigator.userAgent;
    
    // Check for common device patterns
    if (/iPhone/i.test(ua)) return 'iPhone';
    if (/iPad/i.test(ua)) return 'iPad';
    if (/Android/i.test(ua)) {
      const match = ua.match(/Android.*?;\s*([^)]+)/);
      if (match) return match[1].trim();
      return 'Android Device';
    }
    if (/Macintosh/i.test(ua)) return 'Mac';
    if (/Windows/i.test(ua)) return 'Windows PC';
    if (/Linux/i.test(ua)) return 'Linux PC';
    
    return 'Unknown Device';
  }

  /**
   * Get operating system name and version
   */
  private getOSInfo(): { name: string; version: string } {
    const ua = navigator.userAgent;
    
    // iOS
    if (/iPhone|iPad|iPod/i.test(ua)) {
      const match = ua.match(/OS (\d+_\d+_?\d*)/);
      return {
        name: 'iOS',
        version: match ? match[1].replace(/_/g, '.') : 'Unknown'
      };
    }
    
    // Android
    if (/Android/i.test(ua)) {
      const match = ua.match(/Android (\d+\.?\d*\.?\d*)/);
      return {
        name: 'Android',
        version: match ? match[1] : 'Unknown'
      };
    }
    
    // Windows
    if (/Windows/i.test(ua)) {
      const match = ua.match(/Windows NT (\d+\.\d+)/);
      const version = match ? match[1] : 'Unknown';
      return {
        name: 'Windows',
        version: version
      };
    }
    
    // macOS
    if (/Macintosh/i.test(ua)) {
      const match = ua.match(/Mac OS X (\d+_\d+_?\d*)/);
      return {
        name: 'macOS',
        version: match ? match[1].replace(/_/g, '.') : 'Unknown'
      };
    }
    
    // Linux
    if (/Linux/i.test(ua)) {
      return {
        name: 'Linux',
        version: 'Unknown'
      };
    }
    
    return {
      name: 'Unknown',
      version: 'Unknown'
    };
  }

  /**
   * Get browser name and version
   */
  private getBrowserInfo(): { name: string; version: string } {
    const ua = navigator.userAgent;
    
    // Chrome
    if (/Chrome/i.test(ua) && !/Edge|Edg/i.test(ua)) {
      const match = ua.match(/Chrome\/(\d+\.\d+)/);
      return {
        name: 'Chrome',
        version: match ? match[1] : 'Unknown'
      };
    }
    
    // Firefox
    if (/Firefox/i.test(ua)) {
      const match = ua.match(/Firefox\/(\d+\.\d+)/);
      return {
        name: 'Firefox',
        version: match ? match[1] : 'Unknown'
      };
    }
    
    // Safari
    if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) {
      const match = ua.match(/Version\/(\d+\.\d+)/);
      return {
        name: 'Safari',
        version: match ? match[1] : 'Unknown'
      };
    }
    
    // Edge
    if (/Edge|Edg/i.test(ua)) {
      const match = ua.match(/(?:Edge|Edg)\/(\d+\.\d+)/);
      return {
        name: 'Edge',
        version: match ? match[1] : 'Unknown'
      };
    }
    
    return {
      name: 'Unknown',
      version: 'Unknown'
    };
  }

  /**
   * Get user's IP address (simplified - in production you might want to use a service)
   */
  private async getIPAddress(): Promise<string> {
    try {
      // In a real app, you might want to use a service like ipapi.co or ipify
      // For now, return a placeholder
      return '127.0.0.1';
    } catch (error) {
      console.warn('Could not determine IP address:', error);
      return '127.0.0.1';
    }
  }

  /**
   * Generate device fingerprint
   */
  public async generateFingerprint(): Promise<DeviceFingerprint> {
    if (this.fingerprint) {
      return this.fingerprint;
    }

    const deviceId = this.generateDeviceId();
    const deviceName = this.getDeviceName();
    const deviceType = this.getDeviceType();
    const osInfo = this.getOSInfo();
    const browserInfo = this.getBrowserInfo();
    const ipAddress = await this.getIPAddress();

    this.fingerprint = {
      deviceId,
      deviceName,
      deviceType,
      osName: osInfo.name,
      osVersion: osInfo.version,
      browserName: browserInfo.name,
      browserVersion: browserInfo.version,
      ipAddress
    };

    return this.fingerprint;
  }

  /**
   * Get device fingerprint as JSON string for headers
   */
  public async getFingerprintHeader(): Promise<string> {
    const fingerprint = await this.generateFingerprint();
    return JSON.stringify(fingerprint);
  }

  /**
   * Get device fingerprint object
   */
  public async getFingerprint(): Promise<DeviceFingerprint> {
    return await this.generateFingerprint();
  }

  /**
   * Reset fingerprint (useful for testing)
   */
  public reset(): void {
    this.fingerprint = null;
  }
}

// Export singleton instance
export const deviceFingerprintService = DeviceFingerprintService.getInstance();

// Export types
export type { DeviceFingerprint }; 